<template>
  <div class="orders">
    <e-table
      ref="table"
      name="tl"
      :items="items"
      :rowClass="rowClass"
      :highlight="{ hover: false, click: true }"
      :coreColumnDefs="columnDefs"
      :defaultSort="{ prop: 'appt_date', order: 'descending' }"
      :getHeight="getHeight"
      @loadMore="$emit('loadMore')"
    />
  </div>
</template>
<script>
import Vue from "vue";

Vue.component("tl-id", {
  props: { item: Object, options: Object },
  template: `
    <div>
      <popup className="text-sm no-underline" componentPath="components/html" width="900px" :pivot-y="0" :resizable="false" :options="{ url: '/tl_containers/' + item.id }">
        {{item.id}}      
      </popup>     
      <div class="flex items-center justify-center gap-1 py-1" v-if="isAdmin">
          <check-marker :key="prefixKey('marker', item.id)" :item="{id: item.id, marker: item.marker, target: 'order'}" size="16"/>
          <container-colorify :item="{id: item.id, color: item.color }" :key="prefixKey('colorify', item.id)"  size="16"/>
        </div>
    </div>
  `,
});
Vue.component("tl-receivable-invoices", {
  props: { item: Object, options: Object },
  template: `
    <div>
      <div v-for="invoice in item.receivable.invoices" :key="invoice.id">
        <a @click="openUrl('/invoices/' + invoice.id)">{{invoice.number}}</a>
      </div>
    </div>
  `,
});

Vue.component("tl-receivable-amount", {
  props: { item: Object, options: Object },
  template: `
    <div>
      <popup
        :name="item.receivable.charges.amount | toCurrency" className="green" componentPath="components/html" width="720px" height="auto"
        :options="{ title: 'Container: ' + item.id, url: '/containers/' + item.id + '/receivable_charges'}">
      </popup>
    </div>
  `,
});

Vue.component("tl-receivable-payments", {
  props: { item: Object, options: Object },
  template: `
    <div>
      <div v-for="payment in item.receivable.payments" :key="payment.id">
        <a @click="openUrl('/payments/' + payment.id)">{{payment.number}}</a>
      </div>
    </div>
  `,
});

Vue.component("tl-payable-invoices", {
  props: { item: Object, options: Object },
  template: `
    <div>
      <div v-for="invoice in item.payable.invoices" :key="invoice.id">
        <a @click="openUrl('/invoices/' + invoice.id)">{{invoice.number}}</a>
      </div>
    </div>
  `,
});

Vue.component("tl-payable-amount", {
  props: { item: Object, options: Object },
  template: `
    <div>
      <popup
        :name="item.payable.charges.amount | toCurrency" className="red" componentPath="components/html" width="720px" height="auto"
        :options="{ title: 'Container: ' + item.id, url: '/containers/' + item.id + '/payable_charges'}">
      </popup>
    </div>
  `,
});

Vue.component("tl-payable-payments", {
  props: { item: Object, options: Object },
  template: `
    <div>
      <div v-for="payment in item.payable.payments" :key="payment.id">
        <a @click="openUrl('/payments/' + payment.id)">{{payment.number}}</a>
      </div>
    </div>
  `,
});

Vue.component("tl-profit", {
  props: { item: Object, options: Object },
  template: `
    <div>{{profit | toCurrency}}</div>
  `,
  computed: {
    profit() {
      return (
        parseFloat(this.item.receivable.charges.amount) -
        parseFloat(this.item.payable.charges.amount)
      );
    },
  },
});

Vue.component("tl-appt", {
  props: { item: Object, options: Object },
  template: `
    <div class="v-appt-time gap-2" v-if="isAdmin">      
      <container-appointment
        :id="item.id"
        :apptData="{ appt_date: item.appt_date, appt_start: item.appt_start, appt_end: item.appt_end, appt_editable: item.appt_editable }"
        :key="prefixKey('appt-time', item.id)"
      />
      <container-appt-confirm
        v-if="item.appt_confirmed_support"
        :id="item.id" 
        :confirmed="item.appt_confirmed"
        :editable="item.appt_confirmed_editable"
        :key="prefixKey('appt-cfm', item.id)"
      />
    </div>
    <div v-else v-html="item.appointment"/>
  `,
});

Vue.component("tl-priority", {
  props: { item: Object, options: Object },
  template: `
    <container-priority :item="{ id: item.id, priority: item.priority }" :key="prefixKey('priority', item.id)"/>
  `,
});

Vue.component("tl-carriers", {
  props: { item: Object, options: Object },
  template: `
    <div>
      <div class="cname"v-for="(carrier, index) in item.carriers" :key="index">
        {{carrier}}
      </div>
    </div>
  `,
});

import api from "@/default/utils/api";
export default {
  title: "TlTable",
  mixins: [api],
  props: {
    items: {
      type: Array,
      required: true,
    },
  },
  computed: {
    columnDefs() {
      if (this.isAdmin) {
        return this.coreColumnDefs;
      } else {
        const titles = ["Customer", "Trucker", "Admin Comment"];
        return this.coreColumnDefs.filter((col) => !titles.includes(col.title));
      }
    },
  },
  data() {
    return {
      coreColumnDefs: [
        {
          title: "ID",
          field: "id",
          key: "id",
          width: 80,
          classes: "cell-style",
          component: "tl-id",
        },
        {
          title: "Hub",
          field: "hub.name",
          key: "hub",
          width: 100,
          component: "ve-table-column",
        },
        {
          title: "Customer",
          field: "customer.name",
          key: "customer",
          width: 180,
          component: "ve-table-column",
        },
        {
          title: "Refer#",
          field: "reference_no",
          key: "refer_no",
          width: 80,
          component: "ve-table-column",
        },
        {
          title: "Bill",
          field: "billing_type",
          key: "billing_type",
          width: 60,
          classes: "upcase",
          component: "ve-table-column",
        },
        {
          title: "AR INVS",
          field: "receivable_invoice_numbers",
          key: "receivable_invoices",
          width: 75,
          minWidth: 75,
          classes: "cell-style greenBg",
          component: "tl-receivable-invoices",
        },
        {
          title: "AR AMT",
          field: "receivable.charges.amount",
          key: "ar",
          width: 75,
          minWidth: 75,
          classes: "cell-style greenBg",
          component: "tl-receivable-amount",
        },
        {
          title: "AR PYMT",
          field: "receivable_payment_numbers",
          key: "receivable_payments",
          width: 100,
          minWidth: 100,
          classes: "cell-style greenBg",
          component: "tl-receivable-payments",
        },

        {
          title: "AP INVS",
          field: "payable_invoice_numbers",
          key: "payable_invoices",
          width: 75,
          minWidth: 75,
          classes: "cell-style pinkBg",
          component: "tl-payable-invoices",
        },
        {
          title: "AP AMT",
          field: "payable.charges.amount",
          key: "ap",
          width: 75,
          minWidth: 75,
          classes: "cell-style pinkBg",
          component: "tl-payable-amount",
        },
        {
          title: "AP PYMT",
          field: "payable_payment_numbers",
          key: "payable_payments",
          width: 100,
          minWidth: 100,
          classes: "cell-style pinkBg",
          component: "tl-payable-payments",
        },
        {
          title: "Profit",
          field: "charges.profit",
          key: "profit",
          width: 75,
          minWidth: 75,
          component: "tl-profit",
        },
        {
          title: "CONT#",
          field: "container_no",
          key: "container_no",
          width: 100,
          component: "ve-table-column",
        },
        {
          title: "Type",
          field: "ctype",
          key: "ctype",
          width: 80,
          component: "ve-table-column",
        },
        {
          title: "LFD",
          field: "rail_lfd",
          key: "rail_lfd",
          type: "Date",
          width: 80,
          component: "ve-table-column",
        },
        {
          title: "Appt. Time",
          field: "appt_date",
          key: "appt_date",
          width: 135,
          minWidth: 135,
          component: "tl-appt",
          sortBy: "desc",
        },
        {
          title: "PRI",
          field: "priority.value",
          key: "priority",
          width: 50,
          component: "tl-priority",
        },
        {
          title: "Origin",
          field: "origin.name",
          key: "origin",
          width: 150,
          component: "ve-table-column",
        },
        {
          title: "Destination",
          field: "destination.name",
          key: "destination",
          width: 180,
          component: "ve-table-column",
        },
        {
          title: "Carriers",
          field: "carriers[0]",
          key: "carriers",
          width: 150,
          component: "tl-carriers",
        },
        {
          title: "Admin Comment",
          field: "last_admin_comment",
          key: "admin_comment",
          width: 150,
          component: "ve-table-column",
        },
      ],
    };
  },
  methods: {
    getHeight() {
      return document.querySelector("#app > .content").offsetHeight - 140;
    },
    rowClass({ row }) {
      let rowColor = `row-${row.color}`;
      return { [rowColor]: true };
    },
  },
};
</script>
